/**
 * Baby Switcher Modal
 * Allows switching between multiple baby profiles
 */

import { useNavigate } from 'react-router-dom';
import { useBaby } from '@/contexts/BabyContext';
import { Plus, Settings, Check } from 'lucide-react';
import { formatBabyAge } from '@/lib/utils';

interface BabySwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const BabySwitcher = ({ isOpen, onClose }: BabySwitcherProps) => {
  const navigate = useNavigate();
  const { babies, activeBaby, setActiveBaby } = useBaby();

  if (!isOpen) return null;


  const handleSelectBaby = async (babyId: string) => {
    try {
      await setActiveBaby(babyId);
      onClose();
    } catch (error) {
      console.error('Error switching baby:', error);
    }
  };

  const handleAddBaby = () => {
    onClose();
    navigate('/add-baby');
  };

  const handleManageProfiles = () => {
    onClose();
    navigate('/settings');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[55]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-3xl max-h-[80vh] flex flex-col animate-slide-up" style={{ boxShadow: 'var(--shadow-modal)' }}>
        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-muted rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Switch Baby</h2>
        </div>

        {/* Baby List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {babies.map((baby) => {
            const isActive = activeBaby?.id === baby.id;

            return (
              <button
                key={baby.id}
                onClick={() => handleSelectBaby(baby.id)}
                className="w-full p-4 rounded-2xl border-2 transition-all"
                style={isActive
                  ? { borderColor: 'hsl(var(--primary))', background: 'hsl(var(--primary) / 0.08)' }
                  : { borderColor: 'hsl(var(--border))' }}
              >
                <div className="flex items-center gap-3">
                  {/* Baby Avatar */}
                  <div className="flex-shrink-0">
                    {baby.photoURL ? (
                      <img
                        src={baby.photoURL}
                        alt={baby.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center text-xl">
                        {baby.gender === 'boy' ? '👶' : '👧'}
                      </div>
                    )}
                  </div>

                  {/* Baby Info */}
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-foreground">{baby.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatBabyAge(baby.dob)}
                    </p>
                  </div>

                  {/* Checkmark */}
                  {isActive && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'hsl(var(--primary))' }}>
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="px-5 py-4 space-y-3 border-t border-border">
          <button
            onClick={handleAddBaby}
            className="w-full p-4 bg-white border border-border rounded-2xl font-medium text-foreground flex items-center gap-3"
          >
            <Plus className="w-5 h-5 text-muted-foreground" />
            <span>Add New Baby</span>
          </button>

          <button
            onClick={handleManageProfiles}
            className="w-full p-4 bg-white border border-border rounded-2xl font-medium text-foreground flex items-center gap-3"
          >
            <Settings className="w-5 h-5 text-muted-foreground" />
            <span>Manage Profiles</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default BabySwitcher;
